import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FornitoriAdmin } from 'src/app/models/supplier-portal/supplier/fornitori-admin.models';
import { FornitoriAdminMainService } from 'src/app/services/fornitori-admin-main.service';

@Component({
  selector: 'app-fornitori-ordini-list',
  standalone: false,
  templateUrl: './fornitori-ordini-list.component.html',
  styleUrls: ['./fornitori-ordini-list.component.scss'],
})
export class FornitoriOrdiniListComponent implements OnInit {
  public allOrders: FornitoriAdmin.OpenOrder[] = [];
  public allStorico: FornitoriAdmin.OpenOrder[] = [];
  public orders: FornitoriAdmin.OpenOrder[] = [];
  public loading = false;
  public view: 'lavorazione' | 'storico' = 'lavorazione';

  private storicoLoaded = false;

  public searchOrderNumber = '';
  public searchFornitore = '';
  public startDate: Date | null = null;
  public endDate: Date | null = null;

  sortField = '';
  sortDir: 'asc' | 'desc' = 'asc';
  public sortColumn: string | null = null;
  public sortDirection: 'asc' | 'desc' = 'asc';

  // ── Date binding helpers ───────────────────────────────────────
  // `<input type="date">` exchanges values as `yyyy-mm-dd` strings,
  // while `applyView()` works on real `Date` objects. These getters
  // / setters bridge the two so the HTML can use a plain native
  // input (replacing the legacy `<kendo-datepicker>`).
  get startDateStr(): string {
    return this.toInputDate(this.startDate);
  }
  set startDateStr(value: string) {
    this.startDate = this.fromInputDate(value);
  }
  get endDateStr(): string {
    return this.toInputDate(this.endDate);
  }
  set endDateStr(value: string) {
    this.endDate = this.fromInputDate(value);
  }

  private toInputDate(d: Date | null): string {
    if (!d) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
  private fromInputDate(value: string): Date | null {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  // Clear-all shortcut used by the "Pulisci filtri" button.
  public clearFilters(): void {
    this.searchOrderNumber = '';
    this.searchFornitore = '';
    this.startDate = null;
    this.endDate = null;
    this.applyView();
  }

  /** True when any of the four filters is currently narrowing the view. */
  get filtersActive(): boolean {
    return (
      !!this.searchOrderNumber.trim() ||
      !!this.searchFornitore.trim() ||
      !!this.startDate ||
      !!this.endDate
    );
  }

  /**
   * Maps the legacy `ultimo_stato_perc` bracket logic to a single
   * `data-state` value the SCSS uses to colour the progress bar.
   *   < 40 % → low (red)
   *   40–79 % → mid (amber)
   *   80–99 % → high (light blue)
   *   100 % or "storico" view → full (green)
   */
  progressState(o: FornitoriAdmin.OpenOrder): 'low' | 'mid' | 'high' | 'full' {
    if (this.view === 'storico') return 'full';
    const pct = o.ultimo_stato_perc ?? 0;
    if (pct >= 100) return 'full';
    if (pct >= 80) return 'high';
    if (pct >= 40) return 'mid';
    return 'low';
  }

  constructor(
    private readonly fornitoriAdminServ: FornitoriAdminMainService,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.loadOrders();
  }

  loadOrders(): void {
    this.loading = true;
    this.fornitoriAdminServ.getSupplierOpenOrders().subscribe({
      next: (data) => {
        this.allOrders = data;
        this.applyView();
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  loadStorico(): void {
    this.loading = true;
    this.fornitoriAdminServ.getSupplierCloseOrders().subscribe({
      next: (data) => {
        this.allStorico = data;
        this.storicoLoaded = true;
        this.applyView();
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  reload(): void {
    if (this.view === 'storico') {
      this.storicoLoaded = false;
      this.loadStorico();
    } else {
      this.loadOrders();
    }
  }

  setView(v: 'lavorazione' | 'storico'): void {
    this.view = v;
    if (v === 'storico' && !this.storicoLoaded) {
      this.loadStorico();
    } else {
      this.applyView();
    }
  }

  applyView(): void {
    let filtered =
      this.view === 'storico' ? [...this.allStorico] : [...this.allOrders];

    if (this.view === 'lavorazione') {
      filtered = filtered.filter((o) => !o.or_data_chiusura);
    }

    if (this.searchOrderNumber.trim()) {
      const term = this.searchOrderNumber.trim().toLowerCase();
      filtered = filtered.filter(
        (o) =>
          o.numero_ordine?.toLowerCase().includes(term) ||
          o.or_numero_ordine?.toLowerCase().includes(term),
      );
    }

    if (this.searchFornitore.trim()) {
      const term = this.searchFornitore.trim().toLowerCase();
      filtered = filtered.filter(
        (o) =>
          o.or_fornitore?.toLowerCase().includes(term) ||
          o.or_codice_fornitore?.toLowerCase().includes(term),
      );
    }

    if (this.startDate) {
      const from = new Date(this.startDate);
      from.setHours(0, 0, 0, 0);
      filtered = filtered.filter((o) => {
        const d = o.or_data_ordine ? new Date(o.or_data_ordine) : null;
        return d ? d >= from : false;
      });
    }

    if (this.endDate) {
      const to = new Date(this.endDate);
      to.setHours(23, 59, 59, 999);
      filtered = filtered.filter((o) => {
        const d = o.or_data_ordine ? new Date(o.or_data_ordine) : null;
        return d ? d <= to : false;
      });
    }

    if (this.sortColumn) {
      filtered.sort((a: any, b: any) => {
        let valueA = a[this.sortColumn!];
        let valueB = b[this.sortColumn!];

        valueA ??= '';
        valueB ??= '';

        const isDate =
          valueA instanceof Date ||
          valueB instanceof Date ||
          String(this.sortColumn).includes('data');

        if (isDate) {
          valueA = valueA ? new Date(valueA).getTime() : 0;
          valueB = valueB ? new Date(valueB).getTime() : 0;
        }

        const isNumeric =
          (!isNaN(Number(valueA)) && valueA !== '') ||
          (!isNaN(Number(valueB)) && valueB !== '');

        if (isNumeric) {
          valueA = Number(valueA);
          valueB = Number(valueB);
        } else {
          valueA = String(valueA).toLowerCase();
          valueB = String(valueB).toLowerCase();
        }

        let result = 0;

        if (valueA > valueB) result = 1;
        else if (valueA < valueB) result = -1;

        return this.sortDirection === 'asc' ? result : result * -1;
      });
    }

    if (this.sortColumn) {
      filtered.sort((a: any, b: any) => {
        let valueA = a[this.sortColumn!];
        let valueB = b[this.sortColumn!];

        valueA ??= '';
        valueB ??= '';

        const isDate =
          valueA instanceof Date ||
          valueB instanceof Date ||
          String(this.sortColumn).includes('data');

        if (isDate) {
          valueA = valueA ? new Date(valueA).getTime() : 0;
          valueB = valueB ? new Date(valueB).getTime() : 0;
        }

        const isNumeric =
          (!isNaN(Number(valueA)) && valueA !== '') ||
          (!isNaN(Number(valueB)) && valueB !== '');

        if (isNumeric) {
          valueA = Number(valueA);
          valueB = Number(valueB);
        } else {
          valueA = String(valueA).toLowerCase();
          valueB = String(valueB).toLowerCase();
        }

        let result = 0;

        if (valueA > valueB) result = 1;
        else if (valueA < valueB) result = -1;

        return this.sortDirection === 'asc' ? result : result * -1;
      });
    }

    this.orders = filtered;
  }

  sort(field: string): void {
    if (this.sortField === field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDir = 'asc';
    }
  }

  sortIcon(field: string): string {
    if (this.sortField !== field) return '⇅';
    return this.sortDir === 'asc' ? '▲' : '▼';
  }

  openDetail(item: FornitoriAdmin.OpenOrder): void {
    if (this.view === 'storico') {
      this.router.navigate([
        '/fornitori-ordini',
        'storico',
        item.ordine_passivo_id ?? 0,
        item.or_gamma_id ?? 0,
        item.fornitore_certificato_id ?? 0,
      ]);
    } else {
      this.router.navigate([
        '/fornitori-ordini',
        'dettagli',
        item.ordine_passivo_id ?? 0,
        item.or_gamma_id ?? 0,
        item.fornitore_certificato_id ?? 0,
      ]);
    }
  }

  sortBy(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applyView();
  }
}
